import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Filter, KeyRound } from "lucide-react";
import LoadingState from "../../components/ui/loading-state";
import { formatTanggal } from "../../lib/tanggal";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import { BatasDaftarNotice } from "./_shared";
import NotaBelumTerposting from "./NotaBelumTerposting";

// Tab halaman Audit. `?tab=` di URL supaya tautan "miss" di Dashboard Finance
// bisa membuka daftar Nota Belum Terposting langsung (batch UANG-KASIR N0).
const TAB_NBT = "nota-belum-terposting";

export default function FinanceAudit() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === TAB_NBT ? TAB_NBT : "jejak";
  const pilihTab = (t) => {
    const p = new URLSearchParams(params);
    if (t === "jejak") p.delete("tab"); else p.set("tab", t);
    setParams(p, { replace: true });
  };
  return (
    <div className="p-4 md:p-8 space-y-4">
      <div className="flex gap-1 border-b border-slate-200" role="tablist">
        {[["jejak", "Jejak Perubahan", "tab-jejak-perubahan"],
          [TAB_NBT, "Nota Belum Terposting", "tab-nota-belum-terposting"]].map(([k, l, tid]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => pilihTab(k)}
            data-testid={tid}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k
              ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === TAB_NBT ? (
        <div className="space-y-2">
          <div>
            <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Audit</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Nota Belum Terposting</h1>
            <p className="text-sm text-slate-600 mt-1">
              Nota penjualan yang TIDAK punya jurnal aktif di buku besar, beserta alasannya. Posting ulang
              tidak membetulkan angka — nota yang rumusnya timpang tetap gagal sampai datanya dikoreksi.
            </p>
          </div>
          <NotaBelumTerposting dariAwal={params.get("dari") || ""} sampaiAwal={params.get("sampai") || ""} />
        </div>
      ) : (
        <JejakPerubahan />
      )}
    </div>
  );
}

// `GET /finance/audit` mengurut timestamp desc lalu memotong `to_list(limit)`
// di backend, default limit=200 (lihat audit.py) — halaman ini tidak
// mengirim parameter `limit`, jadi 200 itu batasnya. Sort di sini murni
// client-side atas baris yang terlanjur terambil.
const AUDIT_BATAS_SERVER = 200;

const ACTION_STYLE = {
  create:  { c: "bg-emerald-50 text-emerald-700 border-emerald-200", l: "Buat" },
  edit:    { c: "bg-sky-50 text-sky-700 border-sky-200",             l: "Edit" },
  void:    { c: "bg-rose-50 text-rose-700 border-rose-200",          l: "Void" },
  delete:  { c: "bg-rose-50 text-rose-700 border-rose-200",          l: "Hapus" },
  approve: { c: "bg-emerald-50 text-emerald-700 border-emerald-200", l: "Approve" },
  reject:  { c: "bg-rose-50 text-rose-700 border-rose-200",          l: "Reject" },
  bayar_piutang: { c: "bg-sky-50 text-sky-700 border-sky-200",       l: "Bayar Piutang" },
  bayar_utang:   { c: "bg-sky-50 text-sky-700 border-sky-200",       l: "Bayar Utang" },
};

function JejakPerubahan() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [entity, setEntity] = useState("");

  // `GET /finance/audit` TIDAK menerima/menyaring parameter `entity` sama sekali
  // (backend mengabaikannya diam-diam — lihat routers/finance/audit.py) jadi
  // dropdown ini dulu terasa berfungsi padahal tidak pernah benar-benar
  // memfilter apa pun. Diperbaiki jadi filter CLIENT-SIDE atas data yang sudah
  // termuat, memakai field `object_type` (nama field asli dari _audit_log()) —
  // bukan `entity` yang memang tidak pernah dikirim backend.
  const load = () => {
    setLoading(true);
    setLoadError(null);
    api.get("/finance/audit")
      .then(r => setItems(r.data.items || r.data || []))
      .catch((e) => { setLoadError(e); toast.error("Gagal memuat audit"); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const entityOptions = useMemo(
    () => [...new Set(items.map(a => a.object_type).filter(Boolean))].sort(),
    [items]
  );
  const filteredItems = useMemo(
    () => entity ? items.filter(a => a.object_type === entity) : items,
    [items, entity]
  );

  // Urutan bawaan (timestamp desc dari backend) tetap sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const shownItems = useMemo(() => sortRows(filteredItems, sortKey, sortDir, {
    waktu: (a) => a.at || a.timestamp || "",
    user: (a) => a.user || a.user_email || a.by || "",
    aksi: (a) => a.action || "",
    entity: (a) => a.object_type || "",
  }), [filteredItems, sortKey, sortDir]);

  return (
    <div className="space-y-6" data-testid="finance-audit">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Audit Trail</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Jejak Perubahan</h1>
          <p className="text-sm text-slate-600 mt-1">
            Jurnal keuangan tidak pernah dihapus — void berarti jurnal pembalik (reversal), bukan penghapusan.
            Dokumen sumber tertentu (mis. penjualan/akuisisi) bisa dihapus lewat gerbang PIN approval,
            dan aksinya tetap tercatat di log ini.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <select value={entity} onChange={e => setEntity(e.target.value)} data-testid="audit-filter" className="h-10 px-3 border border-slate-200 rounded-lg text-sm">
            <option value="">Semua entity</option>
            {entityOptions.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>

      <BatasDaftarNotice shown={items.length} batas={AUDIT_BATAS_SERVER} />
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <LoadingState loading={loading} error={loadError} onRetry={load} varian="kartu" jumlahKartu={3} data-testid="loading-audit">
        {shownItems.length === 0 ? (
            <div className="p-10 text-center">
              <ShieldCheck className="w-12 h-12 mx-auto text-slate-400 mb-3" />
              <p className="text-slate-600 text-sm">Belum ada aktivitas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <SortTh label="Waktu" sortKeyName="waktu" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="audit-sort-waktu" />
                    <SortTh label="User" sortKeyName="user" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="audit-sort-user" />
                    <SortTh label="Aksi" sortKeyName="aksi" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="audit-sort-aksi" />
                    <SortTh label="Entity" sortKeyName="entity" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="audit-sort-entity" />
                    <th className="px-4 py-2.5 text-left">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {shownItems.map((a, i) => {
                    const st = ACTION_STYLE[a.action] || { c: "bg-slate-50 text-slate-700 border-slate-200", l: a.action };
                    const isDelete = a.action === "delete";
                    return (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-4 py-2 text-xs text-slate-600 font-mono">{formatTanggal(a.at || a.timestamp, { denganJam: true })}</td>
                        <td className="px-4 py-2 text-slate-800 text-xs">{a.user || a.user_email || a.by}</td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${st.c}`}>{st.l}</span>
                            {isDelete && a.hard_delete && (
                              <span data-testid={`audit-hard-delete-${i}`}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-rose-50 text-rose-700 border-rose-200">
                                <KeyRound className="w-3 h-3" /> hard delete (ber-PIN)
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-600">{a.object_type}</td>
                        <td className="px-4 py-2 text-xs text-slate-500 max-w-md">
                          {isDelete && a.narrative ? (
                            <p className="text-rose-700">{a.narrative}</p>
                          ) : (
                            <p className="truncate">{a.reason || (a.after ? JSON.stringify(a.after).slice(0, 90) : "-")}</p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </LoadingState>
      </div>
    </div>
  );
}
